/**
 * Cấu hình cho màn hình Ma trận chuyển trạng thái lịch hẹn.
 *
 * `department_type` gửi lên API chính là ID phòng ban. Chỉ 4 phòng ban dưới đây
 * được cấu hình ma trận; tên hiển thị lấy từ API /api/v1/be/departments, danh
 * sách dưới chỉ dùng làm nhãn dự phòng khi gọi API thất bại.
 */
export const TRANSITION_DEPARTMENT_IDS: number[] = [4, 7, 8, 9];

export const DEPARTMENT_FALLBACK_NAMES: Record<number, string> = {
    4: 'Phòng ban #4',
    7: 'Phòng ban #7',
    8: 'Phòng ban #8',
    9: 'Phòng ban #9',
};

export interface ScreenContextOption {
    value: number;
    label: string;
}

/**
 * Các ngữ cảnh màn hình mà API hỗ trợ, `value` chính là `screen_context`.
 * 4 = Khách hàng: dùng chung cho danh sách và chi tiết khách hàng.
 */
export const SCREEN_CONTEXTS: ScreenContextOption[] = [
    { value: 1, label: 'Lịch hẹn' },
    { value: 2, label: 'Lịch tư vấn' },
    { value: 3, label: 'Lịch thực hiện' },
    { value: 4, label: 'Khách hàng' },
];

export const DEFAULT_SCREEN_CONTEXT = 1;

export function screenContextLabel(value: number): string {
    return SCREEN_CONTEXTS.find((c) => c.value === value)?.label ?? `Màn hình #${value}`;
}

export interface FeatureSettingType {
    /** Mã tính năng, chính là `{type}` trên API /system-feature-settings/by-type */
    type: string;
    name: string;
    description: string;
    /** Danh sách thẻ S nguồn (`origin_customer_tag_ids`) */
    origin?: { label: string; placeholder: string };
    /** Có thẻ S đích (`customer_tag_id`) và cờ đồng bộ hồ sơ khách (`sync_customer`) không */
    target?: boolean;
    /** Có chọn nhân viên Trực page (`staff_id`) và Telesale (`telesale_id`) mặc định không */
    staff?: boolean;
}

/**
 * Các tính năng tự chuyển trạng thái S, cấu hình chung toàn hệ thống (branch_id = null).
 * Thêm tính năng mới thì khai báo thêm ở đây, server tự tạo bản ghi khi lưu lần đầu.
 */
export const FEATURE_SETTING_TYPES: FeatureSettingType[] = [
    {
        type: 'appointment_checkin',
        name: 'Check-in lịch hẹn (Đã đến cơ sở)',
        description: 'Khi lễ tân check-in lịch hẹn, khách đang ở trạng thái nguồn được tự động chuyển sang trạng thái đích.',
        origin: { label: 'Áp dụng cho khách đang ở trạng thái', placeholder: 'Chọn trạng thái được phép check-in' },
        target: true,
    },
    {
        type: 'appointment_default_staff',
        name: 'Nhân viên Trực page & Telesale mặc định',
        description: 'Tự gán cho khách mới đăng ký ở màn check-in: hệ thống nạp vào hồ sơ khách hàng và lịch hẹn mới tạo.',
        staff: true,
    },
    {
        type: 'telesale_customer_list',
        name: 'Thẻ hiện ở Khách tiềm năng',
        description: 'Danh sách /telesales/customer chỉ hiện khách đang ở các thẻ này. Tắt thì hiện tất cả.',
        origin: { label: 'Hiện khách đang ở trạng thái', placeholder: 'Chọn các trạng thái hiện trong danh sách' },
    },
    {
        type: 'telesale_deposit_received',
        name: 'Xác nhận đã nhận cọc',
        description: 'Khi xác nhận đã nhận cọc, khách đang ở trạng thái nguồn tự chuyển sang trạng thái đích. Thẻ đích không nằm trong danh sách Khách tiềm năng thì khách rời danh sách; thẻ đích không thuộc thẻ nguồn check-in thì khi check-in khách không tự sang S7.',
        origin: { label: 'Áp dụng cho khách đang ở trạng thái', placeholder: 'Chọn trạng thái nguồn' },
        target: true,
    },
];
